import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { AdminBorderSystemNormative, UpsertBorderSystemNormativeBody } from "./types/systemNormatives";

export const useSystemNormativesApi = () => {
  const api = useApi();

  const getAdminSystemNormative = useCallback(
    (id: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/system_normatives/${id}`;
      return useSWRMutation<AdminBorderSystemNormative, CustomError, string>(url, (url) =>
        api.get<AdminBorderSystemNormative>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminSystemNormatives = useCallback(
    (systemId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/systems/${systemId}/system_normatives`, query);
      return useSWRImmutable<Paginated<AdminBorderSystemNormative>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminBorderSystemNormative>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminSystemNormatives = useCallback(
    (systemId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/systems/${systemId}/system_normatives`, query);
      return api.get<Paginated<AdminBorderSystemNormative>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminSystemNormative = useCallback(
    async (body: UpsertBorderSystemNormativeBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/system_normatives`;
      const id = await api.post<{ id: string }, UpsertBorderSystemNormativeBody>(url, body);
      mutateCache([url, `/admin/settings/border/systems/${body.systemId}/system_normatives`]);
      return id;
    },
    [api],
  );

  const updateAdminSystemNormative = useCallback(
    async (systemNormativeId: string, body: UpsertBorderSystemNormativeBody): Promise<void> => {
      const url = `/admin/settings/border/system_normatives/${systemNormativeId}`;
      await api.put<UpsertBorderSystemNormativeBody>(url, body);
      mutateCache([
        "/admin/settings/border/system_normatives",
        `/admin/settings/border/systems/${body.systemId}/system_normatives`,
      ]);
    },
    [api],
  );

  const deleteAdminSystemNormative = useCallback(
    async (systemId: string, systemNormativeId: string): Promise<void> => {
      const url = `/admin/settings/border/system_normatives/${systemNormativeId}`;
      await api.del(url).then(() => {
        mutateCache([
          `/admin/settings/border/system_normatives`,
          `/admin/settings/border/systems/${systemId}/system_normatives`,
        ]);
      });
    },
    [api],
  );

  return {
    getAdminSystemNormative,
    useAdminSystemNormatives,
    getAdminSystemNormatives,
    insertAdminSystemNormative,
    updateAdminSystemNormative,
    deleteAdminSystemNormative,
  };
};
